"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';

export interface TemoignageData {
  id: string;
  auteur: string;
  texte: string;
  note: number;
  centreName: string;
}

interface Props {
  temoignages: TemoignageData[];
}

const AVATAR_COLORS = ['#DB052C', '#2563EB', '#059669', '#7C3AED', '#EA580C', '#0891B2'];

function getInitials(name: string): string {
  return name.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

const VISIBLE = 3;

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1" role="img" aria-label={`Note : ${rating} sur 5 étoiles`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill={i < rating ? '#F59E0B' : 'none'}
          stroke={i < rating ? '#F59E0B' : '#D1D5DB'}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ))}
    </div>
  );
}

export default function TestimonialsSection({ temoignages }: Props) {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const maxIndex = Math.max(0, temoignages.length - VISIBLE);

  const goTo = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating]);

  const prev = useCallback(() => {
    goTo(current === 0 ? maxIndex : current - 1);
  }, [current, goTo, maxIndex]);

  const next = useCallback(() => {
    goTo(current >= maxIndex ? 0 : current + 1);
  }, [current, goTo, maxIndex]);

  useEffect(() => {
    intervalRef.current = setInterval(next, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [next]);

  const resetInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(next, 5000);
  };

  const visibleCount = Math.min(VISIBLE, temoignages.length);
  const visibleTemoignages = Array.from({ length: visibleCount }, (_, i) => temoignages[current + i]).filter(Boolean);

  if (temoignages.length === 0) return null;

  return (
    <section
      id="temoignages"
      className="relative z-10 -mt-20 pb-20 md:pb-28 overflow-hidden"
      style={{ backgroundColor: '#FFF0F2' }}
      aria-labelledby="testimonials-title"
    >
      {/* Decorative background blobs */}
      <div className="absolute top-0 left-0 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, #DB052C 0%, transparent 70%)', transform: 'translate(-40%, -40%)' }} aria-hidden="true" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-10 pointer-events-none" style={{ background: 'radial-gradient(circle, #DB052C 0%, transparent 70%)', transform: 'translate(40%, 40%)' }} aria-hidden="true" />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative pt-28 md:pt-32">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Témoignages patients
          </p>
          <h2
            id="testimonials-title"
            className="text-3xl md:text-4xl font-semibold text-text-main mb-3"
            style={{ fontFamily: 'Poppins, sans-serif', letterSpacing: '-0.01em' }}
          >
            Ils nous font confiance
          </h2>
          <span className="section-decorator" aria-hidden="true" />
        </div>

        {/* Testimonials grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}
          role="region"
          aria-live="polite"
          aria-label="Témoignages patients"
        >
          {visibleTemoignages.map((t, idx) => {
            const color = AVATAR_COLORS[(current + idx) % AVATAR_COLORS.length];
            const initials = getInitials(t.auteur);
            return (
              <div
                key={`${current}-${idx}`}
                className="bg-white rounded-2xl shadow-card-hover p-6 md:p-8 relative"
                style={{ borderLeft: '4px solid #DB052C' }}
              >
                {/* Decorative quote mark */}
                <div className="absolute top-4 right-6 text-[80px] leading-none font-serif text-primary opacity-5 select-none pointer-events-none" aria-hidden="true">&ldquo;</div>

                {/* Quote icon */}
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mb-4 shadow-md" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
                    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
                  </svg>
                </div>

                <blockquote>
                  <p
                    className="text-text-main text-sm md:text-base leading-relaxed mb-6 italic"
                    style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}
                  >
                    &ldquo;{t.texte}&rdquo;
                  </p>
                </blockquote>

                <div className="flex flex-col gap-3 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 shadow-sm"
                      style={{ backgroundColor: color, fontFamily: 'Poppins, sans-serif' }}
                      aria-hidden="true"
                    >
                      {initials}
                    </div>
                    <div>
                      <p className="font-semibold text-text-main text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t.auteur}
                      </p>
                      {t.centreName && (
                        <p className="text-text-muted text-xs mt-0.5" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {t.centreName}
                        </p>
                      )}
                    </div>
                  </div>
                  <StarRating rating={t.note ?? 5} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Controls */}
        {temoignages.length > VISIBLE && (
          <div className="flex items-center justify-center gap-6 mt-10">
            <button
              onClick={() => { prev(); resetInterval(); }}
              className="w-10 h-10 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label="Témoignages précédents"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2" role="tablist" aria-label="Navigation des témoignages">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={i === current}
                  aria-label={`Groupe de témoignages ${i + 1}`}
                  onClick={() => { goTo(i); resetInterval(); }}
                  className={`rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    i === current ? 'w-6 h-2.5 bg-primary' : 'w-2.5 h-2.5 bg-border hover:bg-text-muted'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={() => { next(); resetInterval(); }}
              className="w-10 h-10 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label="Témoignages suivants"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
